import React, { useState, useEffect, useCallback } from 'react';
import { ChevronRight, SkipForward } from 'lucide-react';

// Story data for each level transition
const STORY_SCENES = {
  // Intro scene (before level 1)
  0: {
    background: 'linear-gradient(180deg, #1a1a2e 0%, #16213e 50%, #0f0f23 100%)',
    scenes: [
      {
        character: 'commander',
        name: 'COMMANDER BRICK',
        dialogue: "Soldier! The robot army has invaded our city. You're our last hope!",
        emotion: 'serious'
      },
      {
        character: 'player',
        name: 'LEGO SOLDIER',
        dialogue: "I'm ready, Commander. These machines won't know what hit them!",
        emotion: 'determined'
      },
      {
        character: 'commander',
        name: 'COMMANDER BRICK',
        dialogue: "Good luck out there. The fate of Brick City rests on your shoulders.",
        emotion: 'hopeful'
      }
    ]
  },
  // After level 1
  1: {
    background: 'linear-gradient(180deg, #2d132c 0%, #801336 50%, #c72c41 100%)',
    scenes: [
      {
        character: 'player',
        name: 'LEGO SOLDIER',
        dialogue: "First wave cleared! But there's more of them coming...",
        emotion: 'alert'
      },
      {
        character: 'radio',
        name: 'RADIO',
        dialogue: "*static* ...Intel reports robot factories to the east. Push forward!",
        emotion: 'static'
      }
    ]
  },
  // After level 2
  2: {
    background: 'linear-gradient(180deg, #1b1b2f 0%, #162447 50%, #1f4068 100%)',
    scenes: [
      {
        character: 'enemy',
        name: '???',
        dialogue: "Foolish plastic soldier... You cannot stop the Machine Revolution!",
        emotion: 'menacing'
      },
      {
        character: 'player',
        name: 'LEGO SOLDIER',
        dialogue: "Who's there?! Show yourself!",
        emotion: 'surprised'
      },
      {
        character: 'enemy',
        name: 'GENERAL CIRCUIT',
        dialogue: "I am General Circuit. Your city will be converted to scrap!",
        emotion: 'evil'
      }
    ]
  },
  // After level 3
  3: {
    background: 'linear-gradient(180deg, #0c0c0c 0%, #1a1a2e 50%, #2d3436 100%)',
    scenes: [
      {
        character: 'commander',
        name: 'COMMANDER BRICK',
        dialogue: "Soldier, we've intercepted enemy communications. They have a mega weapon!",
        emotion: 'worried'
      },
      {
        character: 'player',
        name: 'LEGO SOLDIER',
        dialogue: "A mega weapon? What kind?",
        emotion: 'concerned'
      },
      {
        character: 'commander',
        name: 'COMMANDER BRICK',
        dialogue: "They call it 'The Deconstructor'. It can disassemble entire buildings!",
        emotion: 'serious'
      }
    ]
  },
  // After level 4
  4: {
    background: 'linear-gradient(180deg, #200122 0%, #6f0000 100%)',
    scenes: [
      {
        character: 'ally',
        name: 'SERGEANT STUD',
        dialogue: "Hey! Over here! I'm part of the resistance!",
        emotion: 'friendly'
      },
      {
        character: 'player',
        name: 'LEGO SOLDIER',
        dialogue: "Resistance? I thought I was alone out here!",
        emotion: 'relieved'
      },
      {
        character: 'ally',
        name: 'SERGEANT STUD',
        dialogue: "Not anymore, friend. Together we can breach their main base!",
        emotion: 'determined'
      }
    ]
  },
  // After level 5
  5: {
    background: 'linear-gradient(180deg, #141e30 0%, #243b55 100%)',
    scenes: [
      {
        character: 'enemy',
        name: 'GENERAL CIRCUIT',
        dialogue: "You've made it far, little brick. But this is where it ends!",
        emotion: 'angry'
      },
      {
        character: 'player',
        name: 'LEGO SOLDIER',
        dialogue: "General Circuit! Face me yourself, coward!",
        emotion: 'brave'
      },
      {
        character: 'enemy',
        name: 'GENERAL CIRCUIT',
        dialogue: "Ha! Very well. Prepare to be... disassembled!",
        emotion: 'evil'
      }
    ]
  },
  // After level 6-9 (shorter scenes)
  6: {
    background: 'linear-gradient(180deg, #232526 0%, #414345 100%)',
    scenes: [
      {
        character: 'radio',
        name: 'RADIO',
        dialogue: "Enemy forces retreating to the industrial zone. Keep pushing!",
        emotion: 'urgent'
      },
      {
        character: 'player',
        name: 'LEGO SOLDIER',
        dialogue: "Roger that. On my way!",
        emotion: 'determined'
      }
    ]
  },
  7: {
    background: 'linear-gradient(180deg, #0f2027 0%, #203a43 50%, #2c5364 100%)',
    scenes: [
      {
        character: 'ally',
        name: 'SERGEANT STUD',
        dialogue: "The robots are building something massive ahead. Be careful!",
        emotion: 'warning'
      },
      {
        character: 'player',
        name: 'LEGO SOLDIER',
        dialogue: "Massive? Like a... giant robot?",
        emotion: 'worried'
      },
      {
        character: 'ally',
        name: 'SERGEANT STUD',
        dialogue: "Worse. A robot factory that builds MORE giant robots!",
        emotion: 'scared'
      }
    ]
  },
  8: {
    background: 'linear-gradient(180deg, #1f1c2c 0%, #928dab 100%)',
    scenes: [
      {
        character: 'commander',
        name: 'COMMANDER BRICK',
        dialogue: "Soldier, you're doing incredible work. The whole city is cheering!",
        emotion: 'proud'
      },
      {
        character: 'player',
        name: 'LEGO SOLDIER',
        dialogue: "Can't stop now, Commander. We're so close!",
        emotion: 'motivated'
      }
    ]
  },
  9: {
    background: 'linear-gradient(180deg, #0f0c29 0%, #302b63 50%, #24243e 100%)',
    scenes: [
      {
        character: 'enemy',
        name: 'GENERAL CIRCUIT',
        dialogue: "Impossible! How are you still functioning?!",
        emotion: 'shocked'
      },
      {
        character: 'player',
        name: 'LEGO SOLDIER',
        dialogue: "Because I've got something you don't - heart! ...And studs.",
        emotion: 'confident'
      }
    ]
  },
  // Boss levels (10, 15, 20, etc.)
  10: {
    background: 'linear-gradient(180deg, #200122 0%, #6f0000 50%, #200122 100%)',
    scenes: [
      {
        character: 'enemy',
        name: 'GENERAL CIRCUIT',
        dialogue: "ENOUGH! Time to end this personally!",
        emotion: 'rage'
      },
      {
        character: 'player',
        name: 'LEGO SOLDIER',
        dialogue: "Bring it on, scrap heap!",
        emotion: 'fierce'
      },
      {
        character: 'narrator',
        name: '',
        dialogue: "The final battle begins...",
        emotion: 'dramatic'
      }
    ]
  },
  15: {
    background: 'linear-gradient(180deg, #000000 0%, #434343 100%)',
    scenes: [
      {
        character: 'enemy',
        name: 'MEGA CIRCUIT',
        dialogue: "You defeated General Circuit, but I am his UPGRADE!",
        emotion: 'menacing'
      },
      {
        character: 'player',
        name: 'LEGO SOLDIER',
        dialogue: "Great. A sequel villain. My favorite.",
        emotion: 'sarcastic'
      }
    ]
  },
  20: {
    background: 'linear-gradient(180deg, #2c3e50 0%, #3498db 100%)',
    scenes: [
      {
        character: 'ally',
        name: 'THE RESISTANCE',
        dialogue: "VICTORY! The robots are retreating! You did it!",
        emotion: 'celebration'
      },
      {
        character: 'player',
        name: 'LEGO SOLDIER',
        dialogue: "We did it. Together. Brick City is safe!",
        emotion: 'happy'
      },
      {
        character: 'commander',
        name: 'COMMANDER BRICK',
        dialogue: "From now on, you'll be known as... THE BRICK LEGEND!",
        emotion: 'proud'
      }
    ]
  },
  // Default for other levels
  default: {
    background: 'linear-gradient(180deg, #1a1a2e 0%, #16213e 100%)',
    scenes: [
      {
        character: 'radio',
        name: 'RADIO',
        dialogue: "More enemies ahead. Stay sharp, soldier!",
        emotion: 'alert'
      },
      {
        character: 'player',
        name: 'LEGO SOLDIER',
        dialogue: "Copy that. Moving forward!",
        emotion: 'ready'
      }
    ]
  }
};

// Character colors and styles
const CHARACTER_STYLES = {
  player: {
    color: '#14F195',
    bgColor: 'rgba(20, 241, 149, 0.1)',
    borderColor: '#14F195'
  },
  commander: {
    color: '#FFD700',
    bgColor: 'rgba(255, 215, 0, 0.1)',
    borderColor: '#FFD700'
  },
  enemy: {
    color: '#FF3B30',
    bgColor: 'rgba(255, 59, 48, 0.1)',
    borderColor: '#FF3B30'
  },
  ally: {
    color: '#5AC8FA',
    bgColor: 'rgba(90, 200, 250, 0.1)',
    borderColor: '#5AC8FA'
  },
  radio: {
    color: '#A1A1AA',
    bgColor: 'rgba(161, 161, 170, 0.1)',
    borderColor: '#A1A1AA'
  },
  narrator: {
    color: '#9945FF',
    bgColor: 'rgba(153, 69, 255, 0.1)',
    borderColor: '#9945FF'
  }
};

const Cutscene = ({ level, onComplete, tokenInfo }) => {
  const [currentScene, setCurrentScene] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const [showSkip, setShowSkip] = useState(false);

  // Get story data for current level
  const storyData = STORY_SCENES[level] || STORY_SCENES.default;
  const currentSceneData = storyData.scenes[currentScene];
  const characterStyle = CHARACTER_STYLES[currentSceneData?.character] || CHARACTER_STYLES.player;

  // Typewriter effect
  useEffect(() => {
    if (!currentSceneData) return;
    
    setDisplayedText('');
    setIsTyping(true);
    
    const text = currentSceneData.dialogue;
    let index = 0;
    
    const typeInterval = setInterval(() => {
      if (index < text.length) {
        setDisplayedText(text.substring(0, index + 1));
        index++;
      } else {
        setIsTyping(false);
        clearInterval(typeInterval);
      }
    }, 30); // 30ms per character
    
    return () => clearInterval(typeInterval);
  }, [currentScene, currentSceneData]);

  // Show skip hint after 1 second
  useEffect(() => {
    const timer = setTimeout(() => setShowSkip(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  // Handle advancing to next scene or completing
  const handleAdvance = useCallback(() => {
    if (isTyping) {
      // If still typing, complete the text immediately
      setDisplayedText(currentSceneData.dialogue);
      setIsTyping(false);
    } else if (currentScene < storyData.scenes.length - 1) {
      // Move to next scene
      setCurrentScene(prev => prev + 1);
    } else {
      // Cutscene complete
      onComplete();
    }
  }, [isTyping, currentScene, storyData.scenes.length, currentSceneData, onComplete]);

  // Handle skip all
  const handleSkip = useCallback(() => {
    onComplete();
  }, [onComplete]);

  // Keyboard controls
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.code === 'Space' || e.code === 'Enter') {
        e.preventDefault();
        handleAdvance();
      } else if (e.code === 'Escape') {
        handleSkip();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleAdvance, handleSkip]);

  if (!currentSceneData) {
    onComplete();
    return null;
  }

  return (
    <div 
      className="fixed inset-0 z-50 flex flex-col"
      style={{ background: storyData.background }}
      onClick={handleAdvance}
      data-testid="cutscene-container"
    >
      {/* Scanlines overlay */}
      <div 
        className="absolute inset-0 pointer-events-none opacity-10"
        style={{
          backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.3) 2px, rgba(0,0,0,0.3) 4px)'
        }}
      />

      {/* Skip button */}
      {showSkip && (
        <button
          onClick={(e) => { e.stopPropagation(); handleSkip(); }}
          className="absolute top-4 right-4 px-4 py-2 bg-black/50 hover:bg-black/70 rounded-lg text-[#A1A1AA] text-sm flex items-center gap-2 transition-colors"
          data-testid="skip-cutscene-btn"
        >
          <SkipForward className="w-4 h-4" />
          SKIP (ESC)
        </button>
      )}

      {/* Level indicator */}
      <div className="absolute top-4 left-4 text-[#A1A1AA] text-sm font-game">
        {level === 0 ? 'PROLOGUE' : `LEVEL ${level} COMPLETE`}
      </div>

      {/* Main content area */}
      <div className="flex-1 flex items-center justify-center p-8">
        {/* Character portrait area */}
        <div className="relative w-32 h-32 mr-8 hidden md:block">
          <div 
            className="w-full h-full rounded-xl flex items-center justify-center text-6xl"
            style={{ 
              backgroundColor: characterStyle.bgColor,
              border: `3px solid ${characterStyle.borderColor}`,
              boxShadow: `0 0 30px ${characterStyle.borderColor}40`
            }}
          >
            {currentSceneData.character === 'player' && '🧱'}
            {currentSceneData.character === 'commander' && '⭐'}
            {currentSceneData.character === 'enemy' && '🤖'}
            {currentSceneData.character === 'ally' && '🛡️'}
            {currentSceneData.character === 'radio' && '📻'}
            {currentSceneData.character === 'narrator' && '📜'}
          </div>
        </div>
      </div>

      {/* Dialogue box */}
      <div className="p-4 md:p-8">
        <div 
          className="max-w-4xl mx-auto rounded-xl p-6 relative"
          style={{ 
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            border: `2px solid ${characterStyle.borderColor}`,
            boxShadow: `0 0 20px ${characterStyle.borderColor}30`
          }}
        >
          {/* Character name */}
          {currentSceneData.name && (
            <div 
              className="absolute -top-4 left-6 px-4 py-1 rounded font-game text-sm"
              style={{ 
                backgroundColor: characterStyle.borderColor,
                color: '#000'
              }}
            >
              {currentSceneData.name}
            </div>
          )}

          {/* Dialogue text */}
          <p 
            className="text-lg md:text-xl leading-relaxed min-h-[80px]"
            style={{ color: characterStyle.color }}
          >
            {displayedText}
            {isTyping && <span className="animate-pulse">▌</span>}
          </p>

          {/* Scene progress indicators */}
          <div className="flex items-center justify-between mt-4">
            <div className="flex gap-2">
              {storyData.scenes.map((_, index) => (
                <div 
                  key={index}
                  className="w-3 h-3 rounded-full transition-colors"
                  style={{ 
                    backgroundColor: index <= currentScene 
                      ? characterStyle.borderColor 
                      : 'rgba(255,255,255,0.2)'
                  }}
                />
              ))}
            </div>

            {/* Continue prompt */}
            {!isTyping && (
              <div className="flex items-center gap-2 text-[#A1A1AA] text-sm animate-pulse">
                <span>Click or press SPACE to continue</span>
                <ChevronRight className="w-4 h-4" />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Token info bar (during level transitions) */}
      {level > 0 && tokenInfo && (
        <div className="p-4 bg-black/50">
          <div className="max-w-4xl mx-auto flex items-center justify-center gap-8 text-sm">
            <div className="text-center">
              <div className="text-[#FFD700] font-game">$SML TOKEN</div>
              <div className="text-[#A1A1AA]">Contract: {tokenInfo.contract?.slice(0, 8)}...</div>
            </div>
            <div className="text-center">
              <div className="text-[#14F195] font-game">PLAY TO EARN</div>
              <div className="text-[#A1A1AA]">Spend tokens in the shop!</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cutscene;
